import React from 'react';

import {CreateDocumentButton} from 'app/components/general/CreateDocumentButton';
import {DashboardPage} from 'app/components/general/DashboardPage';
import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {useQuizzes} from 'app/quizzes/api';
import {CompactQuizListItem} from 'app/quizzes/components/QuizzesDashboard/CompactQuizListItem';
import {useQuizFilters} from 'app/quizzes/hooks';
import {toggleDashboardRelation} from 'app/quizzes/slice';
import {QUIZ_CREATE_PAGE_URL, QUIZZES_HOME} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {
  QuizzesFetchParamsType,
  QuizzesLocationParamsType,
} from 'app/quizzes/types';
import type {WithRouterProps} from 'react-router';
import type {ContentType, QuizSerializedType} from 'sweet-types';

export const QuizzesDashboard = (
  routerProps: WithRouterProps<unknown, QuizzesLocationParamsType>
) => {
  const {isManager} = getCurrentUserPerms(useCurrentUserState().data!);

  const actions = useAsyncActions({toggleDashboardRelation});
  const ct = getContentType('quiz');
  const disabledTooltip = gt(
    'You need to be a team manager to create %s',
    ct.pluralName
  );

  return (
    <DashboardPage<
      QuizSerializedType,
      QuizzesLocationParamsType,
      QuizzesFetchParamsType
    >
      {...routerProps}
      actionArea={
        <CreateDocumentButton
          contentType="quiz"
          disabledTooltip={disabledTooltip}
          disabled={!isManager}
        />
      }
      useDataHook={useQuizzes}
      useFiltersHook={useQuizFilters}
      contentType={'quiz'}
      enableNotifications={true}
      filterbar={({resetFilters, viewingAll}) => (
        <Filterbar
          ct="quiz"
          resetFilters={resetFilters}
          viewingAll={viewingAll}
          show={{
            team: true,
            tags: false,
            sort: true,
            filters: false,
            search: true,
          }}
        />
      )}
      renderItem={(
        quiz,
        {params, refetch},
        isSelected,
        addToSelection,
        removeFromSelection,
        notifications
      ) => (
        <CompactQuizListItem
          key={quiz.id}
          object={quiz}
          refreshData={refetch}
          sortBy={params.ordering}
          notifications={notifications}
          toggleRelation={(contentType: ContentType, isOpen: boolean) =>
            actions.toggleDashboardRelation({
              contentType,
              isOpen,
            })
          }
        />
      )}
      messages={{
        counter: (count) => ngt('%s quiz', '%s quizzes', count),
        notFound: ({viewingAll, resetFilters}, NoResultsMessage) =>
          viewingAll ? (
            <NoResultsMessage
              icon="fa-umbrella"
              head={gt('No quizzes on your account')}
            >
              {isManager ? (
                <FullHTMLLink
                  className="btn btn-primary"
                  html_url={QUIZ_CREATE_PAGE_URL}
                >
                  {gt('Create a new quiz')}
                </FullHTMLLink>
              ) : null}
            </NoResultsMessage>
          ) : (
            <NoResultsMessage
              icon="fa-umbrella"
              head={gt('No quizzes found with these filters')}
            >
              <button
                className="btn-outline-secondary btn btn-sm"
                onClick={resetFilters}
              >
                <i className="fa fa-times" />{' '}
                {gt('Reset filters and view all quizzes')}
              </button>
            </NoResultsMessage>
          ),
      }}
    />
  );
};

const quizCt = getContentType('quiz');

export const QuizzesDashboardBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    icon={quizCt.icon}
    text={gt('Quizzes')}
    link={QUIZZES_HOME}
  />
);
